// MdTiptap.js
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { Markdown } from "tiptap-markdown";
import PropTypes from "prop-types";
import { useEffect } from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";

// Styled wrapper for the editor
const EditorWrapper = styled(Box)(({ theme, error }) => ({
  border: `1px solid ${error ? theme.palette.error.main : theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  height: "100vh - 300px",
  "& .ProseMirror": {
    padding: theme.spacing(2),
    outline: "none",
    minHeight: "100px",
    overflow: "auto",
    fontSize: "0.875rem", // Smaller base font size
    "& p.is-editor-empty:first-of-type::before": {
      content: "attr(data-placeholder)",
      float: "left",
      color: theme.palette.text.secondary,
      pointerEvents: "none",
      height: 0,
    },
    "& h1": {
      fontSize: "1.5rem", // Reduced from 2rem
      fontWeight: "bold",
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    "& h2": {
      fontSize: "1.25rem", // Reduced from 1.5rem
      fontWeight: "bold",
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    "& h3": {
      fontSize: "1.1rem", // Reduced from 1.25rem
      fontWeight: "bold",
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    "& ul": {
      paddingLeft: theme.spacing(3),
      listStyleType: "disc",
    },
    "& ol": {
      paddingLeft: theme.spacing(3),
      listStyleType: "decimal",
    },
    "& li": {
      marginBottom: theme.spacing(0.5),
    },
    "& strong": {
      fontWeight: "bold",
    },
    "& em": {
      fontStyle: "italic",
    },
    "& code": {
      backgroundColor: theme.palette.grey[100],
      padding: "2px 4px",
      borderRadius: "3px",
      fontFamily: "monospace",
      fontSize: "0.8em", // Slightly smaller inline code
    },
    "& blockquote": {
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      paddingLeft: theme.spacing(2),
      marginLeft: 0,
      fontStyle: "italic",
      color: theme.palette.text.secondary,
    },
  },
  "&:focus-within": {
    borderColor: theme.palette.primary.main,
    outline: `2px solid ${theme.palette.primary.main}20`,
  },
}));

const ToolbarWrapper = styled(Box)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1),
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(0.5),
}));

const ToolbarButton = styled("button")(({ theme, isActive }) => ({
  padding: theme.spacing(0.5, 1),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: isActive ? theme.palette.primary.main : theme.palette.background.paper,
  color: isActive ? theme.palette.primary.contrastText : theme.palette.text.primary,
  cursor: "pointer",
  fontSize: "0.75rem", // Smaller toolbar button text
  fontWeight: 500,
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: isActive ? theme.palette.primary.dark : theme.palette.action.hover,
  },
  "&:disabled": {
    opacity: 0.6,
    cursor: "not-allowed",
  },
}));

const MenuBar = ({ editor }) => {
  if (!editor) {
    return null;
  }

  return (
    <ToolbarWrapper>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={!editor.can().chain().focus().toggleBold().run()}
        isActive={editor.isActive("bold")}
      >
        Bold
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editor.can().chain().focus().toggleItalic().run()}
        isActive={editor.isActive("italic")}
      >
        Italic
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        disabled={!editor.can().chain().focus().toggleStrike().run()}
        isActive={editor.isActive("strike")}
      >
        Strike
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleCode().run()}
        disabled={!editor.can().chain().focus().toggleCode().run()}
        isActive={editor.isActive("code")}
      >
        Code
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        isActive={editor.isActive("heading", { level: 1 })}
      >
        H1
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        isActive={editor.isActive("heading", { level: 2 })}
      >
        H2
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        isActive={editor.isActive("heading", { level: 3 })}
      >
        H3
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().setParagraph().run()}
        isActive={editor.isActive("paragraph")}
      >
        Paragraph
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive("bulletList")}
      >
        Bullet List
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive("orderedList")}
      >
        Numbered List
      </ToolbarButton>
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        isActive={editor.isActive("blockquote")}
      >
        Quote
      </ToolbarButton>
    </ToolbarWrapper>
  );
};

export default function MdTipTapDynamicHeight({ value, onChange, outputFormat = "html", error }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: "Inhoud…",
      }),
      Markdown.configure({
        html: true,
        tightLists: true,
        tightListClass: "tight",
        bulletListMarker: "-",
        linkify: false,
        breaks: false,
        transformPastedText: true,
        transformCopiedText: true,
      }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      if (outputFormat === "markdown") {
        const markdown = editor.storage.markdown.getMarkdown();
        onChange(markdown);
      } else if (outputFormat === "json") {
        const json = editor.getJSON();
        onChange(json);
      } else {
        // Default HTML output
        const html = editor.getHTML();
        onChange(html);
      }
    },
    editorProps: {
      attributes: {
        class: "mx-auto focus:outline-none", // Removed larger prose variants
      },
    },
  });

  // Update editor content when value prop changes
  useEffect(() => {
    if (editor && value !== undefined) {
      const currentContent =
        outputFormat === "markdown" ? editor.storage.markdown.getMarkdown() : editor.getHTML();

      if (currentContent !== value) {
        if (outputFormat === "markdown") {
          editor.commands.setContent(value);
        } else {
          editor.commands.setContent(value);
        }
      }
    }
  }, [editor, value, outputFormat]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (editor) {
        editor.destroy();
      }
    };
  }, [editor]);

  return (
    <EditorWrapper error={error}>
      <MenuBar editor={editor} />
      <EditorContent editor={editor} />
    </EditorWrapper>
  );
}

MdTipTapDynamicHeight.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  outputFormat: PropTypes.oneOf(["html", "markdown", "json"]),
  error: PropTypes.string,
};

MenuBar.propTypes = {
  editor: PropTypes.object,
};
